import React, { useState, useEffect, useMemo } from 'react';
import { Contract, Person, LegalEntity } from '../types/Contract';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import {
  generateContractPDF,
  downloadContractPDF,
  printContractPDF,
  isMobileDevice
} from '../utils/documentGenerator';
import './ContractsList.css';

interface ContractsListProps {
  contracts: Contract[];
  onEdit?: (contract: Contract) => void;
  onDelete?: (id: string) => void;
  onStatusChange?: (id: string, status: Contract['status']) => void;
  disabled?: boolean;
}

export const ContractsList: React.FC<ContractsListProps> = ({
  contracts,
  onEdit,
  onDelete,
  onStatusChange,
  disabled = false
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Вспомогательная функция для получения полного имени для поиска
  const getSearchString = (entity: Person | LegalEntity) => {
    if ((entity as any).type === 'legal_entity') {
      const legal = entity as LegalEntity;
      return `${legal.name} ${legal.inn} ${legal.ogrn}`.toLowerCase();
    }
    const person = entity as Person;
    return `${person.lastName} ${person.firstName} ${person.middleName || ''} ${person.passportSeries} ${person.passportNumber}`.toLowerCase();
  };

  // Фильтрация договоров
  const filteredContracts = useMemo(() => {
    return contracts.filter(contract => {
      // Поиск по ФИО/Названию или номеру участка
      const searchLower = searchQuery.toLowerCase();
      const sellerSearch = getSearchString(contract.seller);
      const buyerSearch = getSearchString(contract.buyer);

      const matchesSearch = searchQuery === '' ||
        sellerSearch.includes(searchLower) ||
        buyerSearch.includes(searchLower) ||
        contract.landPlot.cadastralNumber.toLowerCase().includes(searchLower);

      // Фильтр по статусу
      const matchesStatus = statusFilter === 'all' || contract.status === statusFilter;

      // Фильтр по дате (месяц и год)
      const matchesDate = dateFilter === '' ||
        format(new Date(contract.dealDate), 'yyyy-MM') === dateFilter;

      return matchesSearch && matchesStatus && matchesDate;
    });
  }, [contracts, searchQuery, statusFilter, dateFilter]);

  const handleGeneratePDF = (contract: Contract) => {
    try {
      generateContractPDF(contract);
    } catch (error) {
      console.error('Ошибка генерации PDF:', error);
      alert('Ошибка при генерации PDF документа');
    }
  };

  const handleDownloadPDF = (contract: Contract) => {
    try {
      downloadContractPDF(contract);
    } catch (error) {
      console.error('Ошибка скачивания PDF:', error);
      alert('Ошибка при скачивании PDF документа');
    }
  };

  const handlePrintPDF = (contract: Contract) => {
    try {
      printContractPDF(contract);
    } catch (error) {
      console.error('Ошибка печати PDF:', error);
      alert('Ошибка при отправке PDF на печать');
    }
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'dd.MM.yyyy', { locale: ru });
  };

  const formatCurrency = (amount: number | string) => {
    const numAmount = Number(amount);
    if (numAmount === 0) return '0 ₽';

    // Форматируем число с разделителями тысяч
    const formatted = numAmount.toLocaleString('ru-RU');
    return `${formatted} ₽`;
  };

  const getContractTypeText = (type: string) => {
    const typeMap: Record<string, string> = {
      'main': 'Основной',
      'preliminary': 'Предварительный',
      'installment': 'С рассрочкой'
    };
    return typeMap[type] || type;
  };

  const getStatusText = (status: string) => {
    const statusMap: Record<string, string> = {
      'draft': 'Черновик',
      'in_progress': 'В работе',
      'ready_for_registration': 'К регистрации',
      'registered': 'Зарегистрирован',
      'completed': 'Завершен',
      'cancelled': 'Отменен'
    };
    return statusMap[status] || status;
  };

  const handleStatusChange = (contractId: string, newStatus: Contract['status']) => {
    if (onStatusChange) {
      onStatusChange(contractId, newStatus);
    }
  };

  const getDisplayName = (entity: Person | LegalEntity) => {
    if ((entity as any).type === 'legal_entity') {
      return (entity as LegalEntity).name;
    }
    const person = entity as Person;
    return `${person.lastName} ${person.firstName} ${person.middleName || ''}`.trim();
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setDateFilter('');
  };

  const renderSearchAndFilters = () => (
    <div className="search-filters-container">
      {/* Строка поиска */}
      <div className="search-bar">
        <input
          type="text"
          placeholder="Поиск по ФИО или номеру участка..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
        />
        <button
          className="filters-toggle"
          onClick={() => setShowFilters(!showFilters)}
        >
          🔍 Фильтры
        </button>
      </div>

      {/* Панель фильтров */}
      {showFilters && (
        <div className="filters-panel">
          <div className="filters-row">
            <div className="filter-group">
              <label>Статус:</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">Все статусы</option>
                <option value="draft">Черновик</option>
                <option value="in_progress">В работе</option>
                <option value="ready_for_registration">К регистрации</option>
                <option value="registered">Зарегистрирован</option>
                <option value="completed">Завершен</option>
                <option value="cancelled">Отменен</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Месяц сделки:</label>
              <input
                type="month"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="filter-date"
              />
            </div>

            <button
              className="clear-filters-btn"
              onClick={clearFilters}
            >
              Очистить
            </button>
          </div>
        </div>
      )}

      {/* Результаты поиска */}
      <div className="search-results-info">
        {searchQuery || statusFilter !== 'all' || dateFilter ? (
          <span>Найдено: {filteredContracts.length} из {contracts.length}</span>
        ) : (
          <span>Всего: {contracts.length}</span>
        )}
      </div>
    </div>
  );

  const renderMobileCard = (contract: Contract) => (
    <div key={contract.id} className="contract-card">
      <div className="contract-card-header">
        <div className="contract-type-mobile">
          <span className={`contract-type ${contract.type}`}>
            {contract.type === 'main' ? '📄' : contract.type === 'installment' ? '💳' : '⏳'} {getContractTypeText(contract.type)}
          </span>
        </div>
        <div className="contract-status-mobile">
          {onStatusChange ? (
            <select
              className={`status-select ${contract.status}`}
              value={contract.status}
              onChange={(e) => handleStatusChange(contract.id, e.target.value as Contract['status'])}
              disabled={disabled}
            >
              <option value="draft">Черновик</option>
              <option value="in_progress">В работе</option>
              <option value="ready_for_registration">К регистрации</option>
              <option value="registered">Зарегистрирован</option>
              <option value="completed">Завершен</option>
              <option value="cancelled">Отменен</option>
            </select>
          ) : (
            <span className={`status ${contract.status}`}>
              {getStatusText(contract.status)}
            </span>
          )}
        </div>
      </div>

      <div className="contract-card-content">
        <div className="contract-parties">
          <div className="party">
            <div className="party-label">👤 Продавец</div>
            <div className="party-name">{getDisplayName(contract.seller)}</div>
            {contract.seller.phone && <div className="party-phone">{contract.seller.phone}</div>}
          </div>
          <div className="party-arrow">→</div>
          <div className="party">
            <div className="party-label">👤 Покупатель</div>
            <div className="party-name">{getDisplayName(contract.buyer)}</div>
            {contract.buyer.phone && <div className="party-phone">{contract.buyer.phone}</div>}
          </div>
        </div>

        <div className="contract-property">
          <div className="property-info">
            <div className="property-label">🏞️ Участок</div>
            <div className="property-cadastral">{contract.landPlot.cadastralNumber}</div>
            <div className="property-area">{contract.landPlot.area} кв.м</div>
            <div className="property-address">{contract.landPlot.address}</div>
          </div>
        </div>

        <div className="contract-financial">
          <div className="amount-info">
            <div className="amount-label">💰 Сумма сделки</div>
            <div className="amount-value">{formatCurrency(contract.amount)}</div>
          </div>
        </div>

        <div className="contract-dates">
          <div className="date-info">
            <div className="date-label">📅 Дата сделки</div>
            <div className="date-value">{formatDate(contract.dealDate)}</div>
          </div>
          <div className="date-info">
            <div className="date-label">📝 Создан</div>
            <div className="date-value">{formatDate(contract.createdDate)}</div>
          </div>
        </div>
      </div>

      <div className="contract-card-actions">
        {isMobileDevice() ? (
          <>
            <button
              className="btn-action-mobile btn-print"
              onClick={() => handlePrintPDF(contract)}
              disabled={disabled}
              title="Откроется диалог печати"
            >
              🖨️ Печать
            </button>
            <button
              className="btn-action-mobile btn-download"
              onClick={() => handleDownloadPDF(contract)}
              disabled={disabled}
              title="Скачать файл PDF"
            >
              📥 Скачать
            </button>
          </>
        ) : (
          <button
            className="btn-action-mobile btn-print"
            onClick={() => handleGeneratePDF(contract)}
            disabled={disabled}
            title="Откроется диалог печати PDF"
          >
            🖨️ Печать PDF
          </button>
        )}
        {onEdit && (
          <button
            className="btn-action-mobile btn-edit"
            onClick={() => onEdit(contract)}
            disabled={disabled}
          >
            ✏️ Редактировать
          </button>
        )}
        {onDelete && (
          <button
            className="btn-action-mobile btn-delete"
            onClick={() => {
              if (window.confirm('Вы уверены, что хотите удалить этот договор?')) {
                onDelete(contract.id);
              }
            }}
            disabled={disabled}
          >
            🗑️ Удалить
          </button>
        )}
      </div>
    </div>
  );

  if (contracts.length === 0) {
    return (
      <div className="contracts-list">
        <h2>История договоров</h2>
        <div className="empty-state">
          <p>Пока нет созданных договоров</p>
          <p>Создайте первый договор, чтобы увидеть его здесь</p>
        </div>
      </div>
    );
  }

  return (
    <div className="contracts-list">
      <h2>История договоров</h2>

      {/* Поиск и фильтры */}
      {renderSearchAndFilters()}

      {filteredContracts.length === 0 ? (
        <div className="empty-state">
          <p>Не найдено договоров по заданным критериям</p>
          <button className="clear-filters-btn" onClick={clearFilters}>
            Сбросить фильтры
          </button>
        </div>
      ) : (
        <>
          {isMobile ? (
            <div className="contracts-cards-container">
              {filteredContracts.map((contract) => renderMobileCard(contract))}
            </div>
          ) : (
            <div className="contracts-table-container">
              <table className="contracts-table">
                <thead>
                  <tr>
                    <th>Тип</th>
                    <th>Продавец</th>
                    <th>Покупатель</th>
                    <th>Кадастровый номер</th>
                    <th>Площадь (кв.м)</th>
                    <th>Сумма</th>
                    <th>Дата сделки</th>
                    <th>Создан</th>
                    <th>Статус</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredContracts.map((contract) => (
                    <tr key={contract.id}>
                      <td>
                        <span className={`contract-type ${contract.type}`}>
                          {getContractTypeText(contract.type)}
                        </span>
                      </td>
                      <td>
                        <div className="person-info">
                          <div className="name">
                            {getDisplayName(contract.seller)}
                          </div>
                          <div className="phone">{contract.seller.phone}</div>
                        </div>
                      </td>
                      <td>
                        <div className="person-info">
                          <div className="name">
                            {getDisplayName(contract.buyer)}
                          </div>
                          <div className="phone">{contract.buyer.phone}</div>
                        </div>
                      </td>
                      <td>
                        <div className="cadastral-info">
                          <div className="number">{contract.landPlot.cadastralNumber}</div>
                          <div className="address">{contract.landPlot.address}</div>
                        </div>
                      </td>
                      <td>{contract.landPlot.area}</td>
                      <td className="amount">{formatCurrency(contract.amount)}</td>
                      <td>{formatDate(contract.dealDate)}</td>
                      <td>{formatDate(contract.createdDate)}</td>
                      <td>
                        {onStatusChange ? (
                          <select
                            className={`status-select ${contract.status}`}
                            value={contract.status}
                            onChange={(e) => handleStatusChange(contract.id, e.target.value as Contract['status'])}
                            disabled={disabled}
                          >
                            <option value="draft">Черновик</option>
                            <option value="in_progress">В работе</option>
                            <option value="ready_for_registration">К регистрации</option>
                            <option value="registered">Зарегистрирован</option>
                            <option value="completed">Завершен</option>
                            <option value="cancelled">Отменен</option>
                          </select>
                        ) : (
                          <span className={`status ${contract.status}`}>
                            {getStatusText(contract.status)}
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="actions">
                          <button
                            className="btn-action btn-download"
                            onClick={() => handleDownloadPDF(contract)}
                            title="Скачать PDF"
                            disabled={disabled}
                          >
                            📥
                          </button>
                          <button
                            className="btn-action btn-print"
                            onClick={() => handlePrintPDF(contract)}
                            title="Отправить на печать"
                            disabled={disabled}
                          >
                            🖨️
                          </button>
                          {onEdit && (
                            <button
                              className="btn-action btn-edit"
                              onClick={() => onEdit(contract)}
                              title="Редактировать"
                              disabled={disabled}
                            >
                              ✏️
                            </button>
                          )}
                          {onDelete && (
                            <button
                              className="btn-action btn-delete"
                              onClick={() => {
                                if (window.confirm('Вы уверены, что хотите удалить этот договор?')) {
                                  onDelete(contract.id);
                                }
                              }}
                              title="Удалить"
                              disabled={disabled}
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}; 